/* eslint-disable no-console */

import { registerSW } from 'virtual:pwa-register'
import { eventBus } from '@shared/stores/globalState'

/** Update callback function type from registerSW */
type UpdateSWCallback = (reloadPage?: boolean) => Promise<void>

/**
 * COEP 錯誤處理函數
 * 修復跨域請求時的 COEP 錯誤
 */
const handleCOEPErrors = (): void => {
  window.addEventListener('error', (event: ErrorEvent) => {
    // 檢查是否為 COEP 相關錯誤
    if (event.message && (
      event.message.includes('ERR_BLOCKED_BY_RESPONSE.NotSameOriginAfterDefaultedToSameOriginByCoep') ||
      event.message.includes('NotSameOriginAfterDefaultedToSameOriginByCoep')
    )) {
      console.warn('檢測到 COEP 跨源錯誤:', event.message);

      // 防止錯誤傳播
      if (event.preventDefault) {
        event.preventDefault();
      }

      // 如果有明確的目標元素（如圖片），嘗試重新加載
      const target = event.target as HTMLElement | null;
      if (target && target.tagName === 'IMG') {
        const img = target as HTMLImageElement;
        const src = img.src;

        // 使用 fetch 進行跨域請求
        if (src && (src.includes('storage.googleapis.com') ||
                   src.includes('firebasestorage.googleapis.com'))) {
          console.log('嘗試通過 fetch 重新加載資源:', src);

          // 創建請求
          fetch(src, {
            mode: 'cors',
            credentials: 'same-origin',
            headers: {
              'Cross-Origin-Resource-Policy': 'cross-origin'
            }
          })
          .then(response => response.blob())
          .then(blob => {
            // 創建 Object URL 並分配給圖片
            const objectURL = URL.createObjectURL(blob);
            img.src = objectURL;

            // 設置清理函數
            img.onload = (): void => {
              // 加載完成後釋放 Object URL
              URL.revokeObjectURL(objectURL);
            };
          })
          .catch(err => {
            console.error('無法通過 fetch 加載資源:', err);
          });
        }
      }
    }
  }, true);
};

/**
 * 處理 Service Worker 可能的問題
 */
const handleServiceWorkerIssues = (): void => {
  if ('serviceWorker' in navigator) {
    console.log('檢查 Service Worker 問題');

    // 偵測跨域資源請求問題
    window.addEventListener('error', function(e: ErrorEvent) {
      // 檢查是否為圖片或腳本載入錯誤
      const target = e.target as HTMLElement | null;
      if (target && (target.localName === 'img' || target.localName === 'script')) {
        const element = target as HTMLImageElement | HTMLScriptElement;
        const src = 'src' in element ? element.src : '';
        console.warn('資源載入錯誤:', src);

        // 檢查是否為跨域問題
        if (src.includes('googleapis.com') ||
            src.includes('storage.googleapis.com') ||
            src.includes('firebasestorage.googleapis.com')) {
          console.warn('檢測到跨域資源載入問題');

          // 防止錯誤傳播
          e.preventDefault();
          e.stopPropagation();
        }
      }
    }, true);

    // 清理背景同步註冊
    navigator.serviceWorker.ready.then(registration => {
      if ('sync' in registration) {
        const syncManager = registration.sync as SyncManager;
        syncManager.getTags().then(tags => {
          if (tags && tags.length) {
            console.log('清理背景同步標籤:', tags);
            tags.forEach(tag => {
              if (tag.includes('Storage')) {
                syncManager.unregister?.(tag);
              }
            });
          }
        }).catch(err => {
          console.error('無法獲取或清理同步標籤:', err);
        });
      }
    }).catch(err => {
      console.error('無法訪問 Service Worker 註冊:', err);
    });
  }
};

// SyncManager interface extension for unregister method
interface SyncManager {
  getTags(): Promise<string[]>;
  register?(tag: string): Promise<void>;
  unregister?(tag: string): void;
}

if (process.env.NODE_ENV === 'production') {
  // 在註冊 Service Worker 前先處理 COEP 錯誤
  handleCOEPErrors();

  // 監聽 SW 控制權變更（autoUpdate 模式下偵測更新完成）
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    console.log('[SW] 新版本已啟用，通知用戶');
    eventBus.emit('sw:updated');
  });

  const updateSW: UpdateSWCallback = registerSW({
    onNeedRefresh(): void {
      console.log('[SW] 發現新版本');

      // 判斷是否在登入頁（尚未認證）— 直接自動更新，不會丟失資料
      const isLoginPage = !document.querySelector('.app-main, .side-nav, [data-authenticated]');
      if (isLoginPage) {
        console.log('[SW] 登入頁偵測到更新，自動套用新版本');
        updateSW(true);
        return;
      }

      // 已登入 → 顯示更新提示讓用戶選擇時機
      eventBus.emit('sw:needRefresh', updateSW);
    },
    onOfflineReady(): void {
      console.log('應用程式可以離線使用');
      eventBus.emit('sw:offlineReady');
    },
    onRegistered(registration: ServiceWorkerRegistration | undefined): void {
      console.log('Service Worker 已註冊');

      // 檢查 SW 控制的狀態
      if (navigator.serviceWorker.controller) {
        console.log('Service Worker 正在控制頁面');
      }

      // 監聽未處理的錯誤
      window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        // 檢查是否為 Workbox 相關的錯誤
        if (event.reason && typeof event.reason.toString === 'function') {
          const errorString = event.reason.toString();
          if (errorString.includes('workbox') ||
              errorString.includes('no-response') ||
              errorString.includes('queue-replay-failed')) {
            console.warn('Workbox 錯誤:', errorString);

            // 取消阻止事件的默認行為
            event.preventDefault();

            // 不需要重新加載頁面，只需清理緩存
            if ('caches' in window) {
              caches.keys().then(cacheNames => {
                return Promise.all(
                  cacheNames.filter(name => name.includes('workbox')).map(cacheName => {
                    console.log('清理 Workbox 緩存:', cacheName);
                    return caches.delete(cacheName);
                  })
                );
              });
            }
          }
        }
      });
    },
    onRegisterError(error: Error): void {
      console.error('Service Worker 註冊錯誤:', error);
    }
  });

  // 初始化時檢查問題
  handleServiceWorkerIssues();
}
